import { useState } from "react";
import { Send, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface SendToTelegramButtonProps {
  teamId: string;
  text: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Coach-only one-tap button: pushes the prefilled share message into the
 * team's linked Telegram group via the `send-team-telegram` edge function.
 *
 * Only renders when a Telegram chat is linked for the team — see usage in
 * team-card / game-card where it's wrapped in `prefs.telegram_chat_id && ...`.
 */
export function SendToTelegramButton({
  teamId,
  text,
  ariaLabel = "Send to Telegram group",
  disabled = false,
  className,
}: SendToTelegramButtonProps) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || busy || !text) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-team-telegram", {
        body: { team_id: teamId, text },
      });
      if (error) {
        toast.error(error.message || "Couldn't post to Telegram");
      } else if (data?.ok) {
        setSent(true);
        toast.success("Posted to your Telegram group");
        setTimeout(() => setSent(false), 2500);
      } else {
        toast.error(data?.error || "Couldn't post to Telegram");
      }
    } catch (err: any) {
      toast.error(err?.message || "Couldn't post to Telegram");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || busy}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={cn(
        "h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-xl shadow-sm transition-colors",
        "bg-[#229ED9] hover:bg-[#1d8cc1] text-white",
        (disabled || busy) && "opacity-60 cursor-not-allowed",
        className,
      )}
    >
      {busy ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : sent ? (
        <Check className="h-5 w-5" />
      ) : (
        <span className="relative inline-flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" aria-hidden fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
          </svg>
          <Send className="h-2.5 w-2.5 absolute -bottom-0.5 -end-0.5 opacity-0" />
        </span>
      )}
    </button>
  );
}
